import { forwardRef, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { ko } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import "react-datepicker/dist/react-datepicker.css";
import {
  ConfirmButtonWrapper,
  DatePickerContainer,
  DatePickerWrap,
} from "style/admin/dashboard";
import { format } from "date-fns";
import {
  formatWithDay,
  getWeekRangeFromToday,
  isTodayInRange,
} from "utils/dashboard.util";
import { SearchLeftBtnIcon, SearchRightBtnIcon } from "cssReset/icon";

interface FormValues {
  startDate: Date | null;
  endDate: Date | null;
}

interface CustomInputProps {
  value?: string;
  onClick?: () => void;
  startDate: Date | null;
  endDate: Date | null;
}

interface DatePickerProps {
  handleSearchSubmit: (params: { startDate: string; endDate: string }) => void;
  period: string;
}

const DashboardDatepicker = ({
  handleSearchSubmit,
  period,
}: DatePickerProps) => {
  const [start, end] = getWeekRangeFromToday();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    start,
    end,
  ]);
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      startDate: start,
      endDate: end,
    },
  });
  const [startDate, endDate] = dateRange;
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!endDate) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (period === "monthly") {
      const base = new Date(endDate); // ✅ endDate 기준
      const monthStart = new Date(base.getFullYear(), base.getMonth(), 1);
      const monthEnd = new Date(base.getFullYear(), base.getMonth() + 1, 0);

      if (monthEnd > today) monthEnd.setTime(today.getTime());

      setDateRange([monthStart, monthEnd]);

      handleSearchSubmit({
        startDate: format(
          monthStart.setHours(0, 0, 0, 0),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        endDate: format(
          monthEnd.setHours(23, 59, 59, 999),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
      });
    }

    if (period === "weekly") {
      const base = new Date(endDate); // ✅ endDate 기준
      const weekStart = new Date(base);
      weekStart.setDate(base.getDate() - base.getDay()); // 일요일

      const weekEnd = new Date(base);
      if (weekEnd > today) weekEnd.setTime(today.getTime());

      setDateRange([weekStart, weekEnd]);

      handleSearchSubmit({
        startDate: format(
          weekStart.setHours(0, 0, 0, 0),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        endDate: format(
          weekEnd.setHours(23, 59, 59, 999),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
      });
    }

    if (period === "daily") {
      const base = new Date(endDate); // ✅ endDate 기준
      const past = new Date(base);
      past.setDate(base.getDate() - 6);

      const now = new Date();
      if (base > now) base.setTime(now.getTime());

      setDateRange([past, base]);

      handleSearchSubmit({
        startDate: format(past.setHours(0, 0, 0, 0), "yyyy-MM-dd'T'HH:mm:ss"),
        endDate: format(
          base.setHours(23, 59, 59, 999),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
      });
    }
  }, [period]);

  useEffect(() => {
    if (startDate && endDate) {
      handleSearchSubmit({
        startDate: format(
          startDate.setHours(0, 0, 0, 0),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        endDate: format(
          endDate.setHours(23, 59, 59, 999),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
      });
    }
  }, []); // ✅ 최초 마운트 시 한 번만 실행

  const onSubmit = () => {
    if (startDate && endDate) {
      handleSearchSubmit({
        startDate: format(
          startDate.setHours(0, 0, 0, 0),
          "yyyy-MM-dd'T'HH:mm:ss"
        ), // KST 기준 ISO 형태
        endDate: format(
          endDate.setHours(23, 59, 59, 999),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
      });
      setIsOpen(false); // 🔥 달력 닫기
    }
  };

  // 👉 커스텀 캘린더 컨테이너
  const CustomCalendarContainer = ({ children }: any) => {
    return (
      <div className="react-datepicker-wrappers">
        <div className="react-datepicker">{children}</div>
        <ConfirmButtonWrapper>
          <button type="submit">조회</button>
        </ConfirmButtonWrapper>
      </div>
    );
  };

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
    ({ onClick, startDate, endDate }, ref) => {
      let displayText = "";

      if (startDate && endDate) {
        displayText =
          period === "daily"
            ? `${formatWithDay(endDate)}`
            : `${formatWithDay(startDate)} - ${formatWithDay(endDate)}`;
      }

      return (
        <input
          readOnly
          ref={ref}
          className="search-input"
          onClick={onClick}
          value={displayText}
        />
      );
    }
  );

  const handlePrev = () => {
    if (!startDate || !endDate) return;

    let newStart = new Date(startDate);
    let newEnd = new Date(endDate);

    switch (period) {
      case "weekly":
        const prevWeekStart = new Date(startDate);
        prevWeekStart.setDate(prevWeekStart.getDate() - 7);
        prevWeekStart.setHours(0, 0, 0, 0);

        const prevWeekEnd = new Date(prevWeekStart);
        prevWeekEnd.setDate(prevWeekStart.getDate() + 6);
        prevWeekEnd.setHours(23, 59, 59, 999);

        newStart = prevWeekStart;
        newEnd = prevWeekEnd;
        break;
      case "monthly":
        const prevMonthStart = new Date(
          startDate.getFullYear(),
          startDate.getMonth() - 1,
          1
        );
        const prevMonthEnd = new Date(
          prevMonthStart.getFullYear(),
          prevMonthStart.getMonth() + 1,
          0
        );

        newStart = prevMonthStart;
        newEnd = prevMonthEnd;
        break;
      case "daily":
        const prevDailyEnd = new Date(endDate); // 기존 endDate를 기준
        prevDailyEnd.setDate(prevDailyEnd.getDate() - 1); // 하루 전으로 이동

        const prevDailyStart = new Date(prevDailyEnd);
        prevDailyStart.setDate(prevDailyEnd.getDate() - 6); // -6일로 시작 범위

        newStart = prevDailyStart;
        newEnd = prevDailyEnd;
        break;
    }

    setDateRange([newStart, newEnd]);
    handleSearchSubmit({
      startDate: format(newStart.setHours(0, 0, 0, 0), "yyyy-MM-dd'T'HH:mm:ss"), // KST 기준 ISO 형태
      endDate: format(
        newEnd.setHours(23, 59, 59, 999),
        "yyyy-MM-dd'T'HH:mm:ss"
      ),
    });
  };

  const handleNext = () => {
    if (!startDate || !endDate) return;

    let newStart = new Date(startDate);
    let newEnd = new Date(endDate);
    const today = new Date();

    switch (period) {
      case "weekly":
        const nextWeekStart = new Date(startDate);
        nextWeekStart.setDate(nextWeekStart.getDate() + 7);
        nextWeekStart.setHours(0, 0, 0, 0);

        const nextWeekEnd = new Date(nextWeekStart);
        nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
        nextWeekEnd.setHours(23, 59, 59, 999);

        // 오늘 포함 주는 제한
        if (nextWeekEnd > today) {
          nextWeekEnd.setTime(today.getTime());
        }

        newStart = nextWeekStart;
        newEnd = nextWeekEnd;
        break;
      case "monthly":
        const nextMonthStart = new Date(
          startDate.getFullYear(),
          startDate.getMonth() + 1,
          1
        );
        const nextMonthEnd = new Date(
          nextMonthStart.getFullYear(),
          nextMonthStart.getMonth() + 1,
          0
        );

        if (nextMonthEnd > today) {
          nextMonthEnd.setTime(today.getTime()); // 오늘 제한
        }

        newStart = nextMonthStart;
        newEnd = nextMonthEnd;
        break;
      case "daily":
        const nextDailyEnd = new Date(endDate);
        nextDailyEnd.setDate(nextDailyEnd.getDate() + 1); // 하루 뒤

        const nextDailyStart = new Date(nextDailyEnd);
        nextDailyStart.setDate(nextDailyEnd.getDate() - 6);

        if (nextDailyEnd > today) nextDailyEnd.setTime(today.getTime()); // 오늘 제한
        if (nextDailyStart > today) return; // 범위가 오늘 넘으면 막기

        newStart = nextDailyStart;
        newEnd = nextDailyEnd;
        break;
    }

    if (newEnd > today) {
      // 오늘을 초과하면 막기
      return;
    }

    setDateRange([newStart, newEnd]);
    handleSearchSubmit({
      startDate: format(newStart.setHours(0, 0, 0, 0), "yyyy-MM-dd'T'HH:mm:ss"), // KST 기준 ISO 형태
      endDate: format(
        newEnd.setHours(23, 59, 59, 999),
        "yyyy-MM-dd'T'HH:mm:ss"
      ),
    });
  };

  return (
    <DatePickerWrap>
      <button
        type="button"
        onClick={handlePrev}
        className="search-l-r-btn left"
      >
        <SearchLeftBtnIcon />
      </button>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DatePickerContainer>
          <div className="inner">
            <Controller
              name="startDate"
              control={control}
              render={({ field: { onChange, value } }) => (
                <DatePicker
                  locale={ko}
                  selected={period === "daily" ? endDate : startDate} // ✅ 조건부 강조
                  onChange={(date: Date | null) => {
                    if (!date) return;

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (period === "weekly") {
                      const day = date.getDay();
                      const start = new Date(date);
                      start.setDate(date.getDate() - day); // 일요일

                      let end = new Date(start);
                      end.setDate(start.getDate() + 6);

                      if (today >= start && today <= end) {
                        end = today;
                      }

                      setDateRange([start, end]);
                    }

                    if (period === "monthly") {
                      const start = new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        1
                      );
                      const end = new Date(
                        date.getFullYear(),
                        date.getMonth() + 1,
                        0
                      ); // 말일

                      if (end > today) {
                        end.setTime(today.getTime());
                      }

                      setDateRange([start, end]);
                    }

                    if (period === "daily") {
                      const selected = new Date(date); // 사용자가 선택한 날짜 (예: 4월 16일)
                      const past = new Date(selected);
                      past.setDate(selected.getDate() - 6); // 과거 6일 (예: 4월 10일)

                      const now = new Date();
                      if (selected > now) selected.setTime(now.getTime());

                      // 날짜 순서 정렬: 과거 ~ 오늘
                      setDateRange([past, selected]);
                    }
                  }}
                  openToDate={
                    isTodayInRange(startDate, endDate)
                      ? new Date(
                          new Date().getFullYear(),
                          new Date().getMonth() - 1,
                          1
                        )
                      : startDate || new Date()
                  }
                  customInput={
                    <CustomInput startDate={startDate} endDate={endDate} />
                  }
                  dateFormat="yyyy.MM.dd"
                  className="search-input"
                  placeholderText="날짜 범위 선택"
                  monthsShown={period === "monthly" ? 1 : 2}
                  withPortal={false} // 데스크탑이면 false 권장
                  showWeekPicker={period === "weekly"}
                  calendarContainer={CustomCalendarContainer}
                  maxDate={new Date()} // 🔥 오늘까지만 선택 가능
                  open={isOpen}
                  onInputClick={() => setIsOpen(true)}
                  readOnly
                  showMonthYearPicker={period === "monthly"}
                  dayClassName={(date) => {
                    if (period === "daily" && endDate) {
                      const rangeStart = new Date(endDate);
                      rangeStart.setDate(endDate.getDate() - 7);

                      if (date >= rangeStart && date <= endDate) {
                        return "custom-daily-range"; // ✅ 이 클래스는 styled-component나 CSS에서 정의
                      }
                    }
                    return ""; // 기본
                  }}
                />
              )}
            />
          </div>
        </DatePickerContainer>
      </form>
      <button
        type="button"
        onClick={handleNext}
        disabled={isTodayInRange(startDate, endDate)}
        className="search-l-r-btn right"
      >
        <SearchRightBtnIcon />
      </button>
    </DatePickerWrap>
  );
};

export default DashboardDatepicker;
